import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * useSectionProgress — generic progress hook for JavaScript, TypeScript,
 * and System Design sections.
 *
 * Follows the same pattern as usePythonProgress:
 *  - localStorage persistence so progress survives browser sessions
 *  - Debounced PATCH to backend when authenticated
 *  - Merge-on-login: local progress is PATCHed ($addToSet union), then the
 *    merged result is fetched back
 *  - Graceful degradation: works offline / unauthenticated from localStorage
 *
 * @param section — 'javascript' | 'typescript' | 'system-design'
 */

interface SectionProgress {
  completedLessons: string[];
}

interface UseSectionProgressReturn {
  progress: SectionProgress;
  markLessonComplete: (lessonId: string) => void;
  isLessonComplete: (lessonId: string) => boolean;
  isLoading: boolean;
  completedCount: number;
}

const DEBOUNCE_MS = 500;
const FETCH_TIMEOUT_MS = 5000;

function getStorageKey(section: string): string {
  return `section-progress-${section}`;
}

function getEndpoint(section: string): string {
  return `/progress/section/${section}`;
}

function loadFromStorage(section: string): SectionProgress {
  try {
    const raw = localStorage.getItem(getStorageKey(section));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        completedLessons: Array.isArray(parsed.completedLessons)
          ? parsed.completedLessons
          : [],
      };
    }
  } catch {
    // ignore parse errors
  }
  return { completedLessons: [] };
}

function saveToStorage(section: string, progress: SectionProgress): void {
  try {
    localStorage.setItem(getStorageKey(section), JSON.stringify(progress));
  } catch {
    // ignore storage errors
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Timeout')), ms);
  });
  promise.catch(() => { });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function useSectionProgress(section: string): UseSectionProgressReturn {
  const { state } = useAuth();
  const isAuthenticated = state.isAuthenticated;
  const authLoading = state.isLoading;

  const [progress, setProgress] = useState<SectionProgress>(() => loadFromStorage(section));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<string[]>([]);
  const hasSyncedRef = useRef<boolean>(false);

  // Merge-on-login: PATCH local to server, then GET merged result
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      hasSyncedRef.current = false;
      setIsLoading(false);
      return;
    }

    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    let cancelled = false;

    const sync = async () => {
      try {
        const local = loadFromStorage(section);
        const endpoint = getEndpoint(section);

        // Push local progress to server (addToSet merge)
        if (local.completedLessons.length > 0) {
          await api.patch(endpoint, { completedLessons: local.completedLessons });
        }

        // Fetch merged result
        const data = await withTimeout(api.get(endpoint), FETCH_TIMEOUT_MS);
        if (cancelled) return;

        const serverLessons = Array.isArray(data?.completedLessons)
          ? data.completedLessons
          : [];

        const merged: SectionProgress = {
          completedLessons: Array.from(new Set([...local.completedLessons, ...serverLessons])),
        };

        setProgress(merged);
        saveToStorage(section, merged);
      } catch {
        // Graceful degradation — operate from localStorage
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void sync();

    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading, section]);

  // Debounced PATCH
  const schedulePatch = useCallback(
    (lessonId: string) => {
      if (!isAuthenticated) return;

      if (!pendingRef.current.includes(lessonId)) {
        pendingRef.current.push(lessonId);
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        const ids = pendingRef.current;
        pendingRef.current = [];
        if (ids.length === 0) return;
        try {
          await api.patch(getEndpoint(section), { completedLessons: ids });
        } catch {
          // Swallow — graceful degradation
        }
      }, DEBOUNCE_MS);
    },
    [isAuthenticated, section],
  );

  const markLessonComplete = useCallback(
    (lessonId: string) => {
      // Support uncompleting: if lessonId starts with __UNCOMPLETE__ prefix, remove it
      if (lessonId.startsWith('__UNCOMPLETE__')) {
        const realId = lessonId.replace('__UNCOMPLETE__', '');
        setProgress((prev) => {
          const updated: SectionProgress = {
            completedLessons: prev.completedLessons.filter((id) => id !== realId),
          };
          saveToStorage(section, updated);
          return updated;
        });
        // Sync removal to backend
        if (isAuthenticated) {
          api.delete(getEndpoint(section), { body: JSON.stringify({ completedLessons: [realId] }), headers: { 'Content-Type': 'application/json' } }).catch(() => { });
        }
        return;
      }

      setProgress((prev) => {
        if (prev.completedLessons.includes(lessonId)) return prev;
        const updated: SectionProgress = {
          completedLessons: [...prev.completedLessons, lessonId],
        };
        saveToStorage(section, updated);
        schedulePatch(lessonId);
        return updated;
      });
    },
    [section, schedulePatch],
  );

  const isLessonComplete = useCallback(
    (lessonId: string) => progress.completedLessons.includes(lessonId),
    [progress.completedLessons],
  );

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    progress,
    markLessonComplete,
    isLessonComplete,
    isLoading,
    completedCount: progress.completedLessons.length,
  };
}

export default useSectionProgress;
